import { useRoute } from "@react-navigation/native";
import { useNavigation } from "@react-navigation/native";
import { some } from "lodash";
import { CSSProperties, FC, useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { fonts } from "src/fonts";
import { useVendorSetupAccount } from "src/hooks/modals";
import { useIsMobile } from "src/hooks/ui";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { sendInfo, useFetchCompanySettings } from "src/redux/AdminSettings/actions";
import { getDefaultBrandProfile } from "src/redux/company/defaultData";
import { tAddressSettingsPath, tBrandSettingsPath } from "src/redux/company/types";
import { handleErrorEvents } from "src/redux/errorHandler/actions";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import { tReduxState } from "src/redux/types";
import { aBrandCategories, aShippingLeadTime } from "src/util/comboBoxData";
import { bound } from "src/util/functions";
import { formatInputPercent } from "src/util/gFormatting";
import { tBrand, tHeroImage, tVendorData } from "tmt_api/src/types/types.validator";
import { NumberInput, Spacer, StandardButton } from "../components";
import { Import_Sm } from "../components/impComponents";
import {
  deleteCarouselImage,
  replaceCarouselImage,
  uploadBannerImage,
  uploadCarouselImage,
  uploadLogoImage,
  uploadTermsAndConditions,
} from "../redux/company/actions";
import AdminUserPanel from "./AdminUserPanel";
import BrandEthosOptions from "./BrandEthosOptions";
import { FormComboBoxInput, ValidatedComboBoxInput, ValidatedTextFormInput } from "./FormInputs";
import { PdButton, PdButtonLabel } from "./forms/button";
import { PdImagePicker, tImagePickerValue } from "./forms/picker";
import { PdStack } from "./layout";
import { PdText } from "./layout/typography";
import ShimmerView from "./ShimmerView";
import UserSettings from "./UserSettings";
import { YesNo } from "./YesNo";
import { openUrlInNewTab } from "src/util/linking";
import { PdTruck } from "./Icons";
import { getDefaultAddress } from "src/util/user";

type tAdminBrandSettingsParams = {
  params: { brandId?: string };
  key: string;
  name: string;
};

const formatPercentNumber = (value: number) => {
  return Number.parseFloat(formatInputPercent(value * 100));
};

const NumberFormEntry = ({
  myTitle,
  value,
  onChangeNumber,
}: {
  myTitle: string;
  value: string;
  onChangeNumber: (text: number) => void;
}) => {
  const [sIsLoaded, _setIsLoaded] = useState<boolean>(true);
  return (
    <View style={styles.P09_Column}>
      <Text style={styles.P09_FormTitle}>{myTitle}</Text>
      <ShimmerView loading={!sIsLoaded} style={styles.inputShimmer}>
        <NumberInput
          unit={{ label: "$", location: "start" }}
          style={styles.gFormInput}
          value={value}
          onChangeNumber={onChangeNumber}
        />
      </ShimmerView>
    </View>
  );
};

const brandNameSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.name : getDefaultBrandProfile().name;

const contactPersonSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.contactPerson : getDefaultBrandProfile().contactPerson;

const websiteSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.website : getDefaultBrandProfile().website;

const instagramSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.links?.instagram ?? ""
    : getDefaultBrandProfile().links?.instagram ?? "";

const facebookSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.links.facebook ?? ""
    : getDefaultBrandProfile().links.facebook ?? "";

const primaryCategorySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.primaryCategory : getDefaultBrandProfile().primaryCategory;

const cityBasedSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.cityBased : getDefaultBrandProfile().cityBased;

const brandStorySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.brandStory : getDefaultBrandProfile().brandStory;

const address1Selector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.addresses[0].address1
    : getDefaultBrandProfile().addresses[0].address1;

const address2Selector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.addresses[0].address2
    : getDefaultBrandProfile().addresses[0].address2;

const citySelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.addresses[0].city
    : getDefaultBrandProfile().addresses[0].city;

const stateOrProvinceSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.addresses[0].state_or_province
    : getDefaultBrandProfile().addresses[0].state_or_province;

const postalCodeSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.addresses[0].postal_code
    : getDefaultBrandProfile().addresses[0].postal_code;

const shippingLeadTimeSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand"
    ? AdminSettingReducer.shippingLeadTime
    : getDefaultBrandProfile().shippingLeadTime;

const taxIdSelector = ({ AdminSettingReducer }: tReduxState) =>
  AdminSettingReducer.type === "brand" ? AdminSettingReducer.taxId : getDefaultBrandProfile().taxId;

const FormImages: FC<{ id: string; value: tHeroImage[] }> = ({ id, value }) => {
  const dispatch = useAppDispatch();
  const [_value, setValue] = useState(value.map((image) => image.downloadURL) ?? []);
  const [isLoading, setLoading] = useState<{ [index: number]: boolean }>({});
  const anyLoading = useMemo(() => some(isLoading, (val) => val), [isLoading]);
  const canAdd = _value.length < 9;
  const arrayLength = bound(0, 3, 3 - _value.length);
  const tempImageArray = Array.apply("", Array(arrayLength));

  const handleOnAdd = async ({ uri, file, action }: tImagePickerValue) => {
    if (action === "change") {
      setValue((prev) => (uri ? [...prev, uri] : prev));
      setLoading((prev) => ({ ...prev, [_value.length]: true }));
      await dispatch(uploadCarouselImage({ id, images: value, file }));
      setLoading((prev) => ({ ...prev, [_value.length]: false }));
    }
  };

  const handleOnChange = async ({ uri, file, action, index }: tImagePickerValue & { index: number }) => {
    if (action === "change") {
      setValue((prev) => prev.map((prevImage, prevIndex) => (prevIndex === index ? uri || "" : prevImage)));
      setLoading((prev) => ({ ...prev, [index]: true }));
      await dispatch(replaceCarouselImage({ id, index, images: value, file }));
      setLoading((prev) => ({ ...prev, [index]: false }));
    } else if (action === "remove") {
      dispatch(deleteCarouselImage({ index, images: value }));
      setValue((prev) => prev.filter((_prevImage, prevIndex) => prevIndex !== index));
    }
  };

  return (
    <ScrollView
      scrollEnabled
      showsHorizontalScrollIndicator
      horizontal
      style={[styles.row, styles.mt3, styles.P09_Scroll]}
      contentContainerStyle={styles.P09_ScrollContentContainer}
    >
      {_value?.map((uri, index) => (
        <PdImagePicker
          key={uri}
          style={[styles.P09_ImageSelectSquare, styles.margin]}
          mobileStyle={styles.P09_ImageSelectSquare}
          webStyle={webStyles.P09_ImageSelectSquare}
          source={{ uri }}
          onChange={(props) => handleOnChange({ ...props, index })}
          allowDelete={!anyLoading}
          isLoading={isLoading[index]}
          showFallbackLabel
        />
      ))}
      {canAdd && (
        <PdImagePicker
          style={[styles.P09_ImageSelectSquare, styles.margin]}
          mobileStyle={styles.P09_ImageSelectSquare}
          webStyle={webStyles.P09_ImageSelectSquare}
          onChange={handleOnAdd}
          isDisabled={anyLoading}
          placeholder={anyLoading ? "Please wait until image fully load" : "Upload Image"}
        />
      )}
      {tempImageArray.map((_, index) => (
        <View style={styles.imageBlank} key={index.toString()} />
      ))}
    </ScrollView>
  );
};

const Content: FC<{ brandSettings: tBrand & { type: "brand" } }> = ({ brandSettings }) => {
  const dispatch = useAppDispatch();
  const kNav = useNavigation();
  const rUser = useSelector((state) => state.AdminUserReducer);
  const rIsProfileUpdating = useSelector((state) => state.LoadingReducer.profileUpdating);
  const rIsAdmin = useSelector((state) => state.UserReducer.profile.accountType === "tmtAdmin");
  const rTermsAndConditionsSampleUrl = useSelector((state) => state.ConfigsReducer.data.urls?.termsAndConditionsSample);
  const rVendorId = useSelector((state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.vendorId);
  const rVendorIdAdmin = useSelector(
    (state) => state.AdminSettingReducer.type === "brand" && !!state.AdminSettingReducer.vendorId,
  );
  const rEstimatingShippingUrl = useSelector((state) => state.ConfigsReducer.data.urls?.estimatingShipping);
  const rIsMobile = useIsMobile();
  const vendorSetupAccount = useVendorSetupAccount();

  const [sIsValid, setsIsValid] = useState<{ [key: string]: boolean }>({});

  const isSetupPayments = rVendorId || rVendorIdAdmin;

  const handleOnPressViewMyStore = () => {
    kNav.navigate(kRoute.P100, { brand: brandSettings.name });
  };

  const handleOnChangeBannerImage = async ({ file }: tImagePickerValue) => {
    await dispatch(uploadBannerImage(brandSettings.id, file));
    dispatch({
      type: "RIBBON",
      message: brandSettings.images.bannerDownloadURL
        ? "Successfully uploaded banner image."
        : "Successfully uploaded banner image. Be sure to Save before leaving page.",
    });
  };

  const handleOnChangeLogoImage = async ({ file }: tImagePickerValue) => {
    await dispatch(uploadLogoImage(brandSettings.id, brandSettings.type, file));
    dispatch({
      type: "RIBBON",
      message: brandSettings.images.logoDownloadURL
        ? "Successfully uploaded logo image."
        : "Successfully uploaded logo image. Be sure to Save before leaving page.",
    });
  };

  const onPressTermsAndConditions = async () => {
    try {
      if (!brandSettings.termsAndConditions?.url) return;
      openUrlInNewTab(brandSettings.termsAndConditions?.url);
    } catch (e) {
      if (e instanceof Error) dispatch(handleErrorEvents(e.message, "error", e));
    }
  };

  const onPressUploadTOS = () => {
    dispatch(uploadTermsAndConditions(brandSettings.id, brandSettings.type));
  };

  const onChangeOpeningMin = useCallback(
    (myOpeningMin) => dispatch({ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { openingMin: myOpeningMin } }),
    [dispatch],
  );

  const onChangeReorderMin = useCallback(
    (myReorderMin) => dispatch({ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { reorderMin: myReorderMin } }),
    [dispatch],
  );

  const onChangeDefaultShipPercent = useCallback(
    (defaultShipPercent) =>
      dispatch({ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { defaultShipPercent: defaultShipPercent / 100 } }),
    [dispatch],
  );

  const handleBrandPublish = () => {
    dispatch({
      type: "ADMIN_UPDATE_BRAND_SETTINGS",
      settingsPart: { pauseMode: "" },
    });
  };

  const handleBrandHide = () => {
    dispatch({
      type: "ADMIN_UPDATE_BRAND_SETTINGS",
      settingsPart: { pauseMode: "pause" },
    });
  };

  const handleOnPressSetupAccount = () => {
    const address = getDefaultAddress(brandSettings?.addresses ?? []);
    const defaultValue: tVendorData = {
      businessName: brandSettings?.name ?? "",
      businessDomain: brandSettings?.website ?? "",
      ein: brandSettings?.taxId ?? "",
      url: brandSettings?.website ?? "",
      emailAddress: brandSettings?.contacts[0]?.email ?? "",
      address1: address.address1,
      address2: address.address2 ?? "",
      city: address.city,
      state: address.state_or_province,
      zipCode: address.postal_code,
      phone: brandSettings?.contacts[0]?.phone ?? "",
      country: address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    vendorSetupAccount.openAsync({ mode: "brand", defaultValue, isEditing: isSetupPayments });
  };

  return (
    <ScrollView style={styles.P09_StackScreenDefault}>
      <View style={[styles.P09_PageContainer, styles.P09_ZeroTop]}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={[fonts.H2_BrandDark, styles.P08_PageHeader]}>Public Profile Information</Text>
            <Text style={fonts.bodyBlack}>
              This information will be visible to retailers when browsing your brand page
            </Text>
          </View>
          <PdButton onPress={handleOnPressViewMyStore}>
            <PdButtonLabel>View My Store</PdButtonLabel>
          </PdButton>
        </View>
        <View style={styles.formControl}>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { name: "" } }}
              dispatchPath="settingsPart.name"
              selectorFunction={brandNameSelector}
              uniqueName="name"
              controller={sIsValid}
              setController={setsIsValid}
              myTitle="Brand Name*"
              rules={{ required: { message: "Required" } }}
              textInputProps={{
                placeholder: "Brand Name",
                style: styles.gLinkBankInput,
              }}
            />
            <Spacer />
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { contactPerson: "" } }}
              dispatchPath="settingsPart.contactPerson"
              selectorFunction={contactPersonSelector}
              uniqueName="contactPerson"
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Contact Person",
                style: styles.gLinkBankInput,
              }}
              myTitle="Contact Person*"
              rules={{ required: { message: "Required" } }}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { website: "" } }}
              dispatchPath="settingsPart.website"
              selectorFunction={websiteSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Paste a link to your company's website...",
                style: styles.gLinkBankInput,
              }}
              uniqueName="website"
              rules={{
                required: { message: "Required" },
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              myTitle="Website*"
            />
            <Spacer />
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_BRAND_SETTINGS",
                settingsPart: { links: { instagram: "" } },
              }}
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              dispatchPath="settingsPart.links.instagram"
              selectorFunction={instagramSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Paste a link to your company's Instagram account...",
                style: styles.gLinkBankInput,
              }}
              uniqueName="insta"
              myTitle="Instagram"
            />
            <Spacer />
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_BRAND_SETTINGS",
                settingsPart: { links: { facebook: "" } },
              }}
              dispatchPath="settingsPart.links.facebook"
              selectorFunction={facebookSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Paste a link to your company's Facebook account...",
                style: styles.gLinkBankInput,
              }}
              uniqueName="facebook"
              rules={{
                pattern: {
                  pattern: /^(http:\/\/|https:\/\/).*$/g,
                  message: "Please add https:// or http:// before your URL",
                },
              }}
              myTitle="Facebook"
            />
          </View>
          <View style={[styles.gFormRowNoMargin, styles.P09_ZTop]}>
            <View style={[styles.P09_Column, styles.P09_ZTop]}>
              <FormComboBoxInput<tBrandSettingsPath>
                titleStyle={styles.P08_01_InputHeader}
                wrapperStyle={styles.gLinkBankFormItemNoMarginZIndexSingle}
                myTitle="Primary Category"
                placeholder={"Select Primary Category"}
                ComboOptions={aBrandCategories}
                dispatchAction={{
                  type: "ADMIN_UPDATE_BRAND_SETTINGS",
                  settingsPart: { primaryCategory: "Other" },
                }}
                dispatchPath="settingsPart.primaryCategory"
                selectorFunction={primaryCategorySelector}
              />
            </View>
            <Spacer />
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { cityBased: "" } }}
              dispatchPath="settingsPart.cityBased"
              selectorFunction={cityBasedSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "City",
                style: styles.gLinkBankInput,
              }}
              uniqueName="cityBased"
              myTitle="City Based out of"
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tBrandSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { brandStory: "" } }}
              dispatchPath="settingsPart.brandStory"
              selectorFunction={brandStorySelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Brand Story",
                style: styles.gLinkBankInputBox,
                numberOfLines: 8,
                multiline: true,
              }}
              uniqueName="brandStory"
              myTitle="Brand Story*"
              rules={{ required: { message: "Required" } }}
              shimmerStyle={styles.P09_DoubleShimmer}
            />
          </View>
          <BrandEthosOptions />
        </View>
        <View style={styles.formControl}>
          <View style={styles.P09_Column}>
            <PdImagePicker
              style={styles.P09_Banner}
              mobileStyle={styles.P09_BannerImage}
              webStyle={webStyles.P09_BannerImage}
              source={{ uri: brandSettings.images.bannerDownloadURL }}
              onChange={handleOnChangeBannerImage}
              placeholder="Upload Banner Image"
            />
            <View style={styles.P09_ProfileImageContainerValid}>
              <PdImagePicker
                style={styles.P09_ImageSelectRound}
                mobileStyle={styles.P09_ImageRound}
                webStyle={webStyles.P09_ImageRound}
                source={{ uri: brandSettings.images.logoDownloadURL }}
                onChange={handleOnChangeLogoImage}
                placeholder="Upload Logo Image"
              />
            </View>
          </View>
        </View>
      </View>
      <View style={styles.P09_HeroImages}>
        <Text style={styles.inputHeaderCarouselImage}>Carousel Images*</Text>
        <FormImages id={brandSettings.id} value={brandSettings.images.heroes} />
      </View>
      <View style={styles.P09_PageContainer}>
        <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
          <View style={styles.rowColumn}>
            <Text style={styles.gFormHeader}>Brand Information</Text>
          </View>
        </View>
        <View style={styles.formControl}>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tAddressSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_ADDRESS",
                address: { address1: "" },
              }}
              dispatchPath={"address.address1"}
              selectorFunction={address1Selector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Address line 1",
                style: styles.gLinkBankInput,
              }}
              uniqueName="addresses0address1"
              myTitle="Address*"
              rules={{ required: { message: "Required" } }}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tAddressSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_ADDRESS",
                address: { address2: "" },
              }}
              dispatchPath={"address.address2"}
              selectorFunction={address2Selector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Address line 2",
                style: styles.gLinkBankInput,
              }}
              uniqueName="addresses0address2"
              myTitle="Address*"
              rules={{ required: { message: "Required" } }}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tAddressSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_ADDRESS",
                address: { city: "" },
              }}
              dispatchPath={"address.city"}
              selectorFunction={citySelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "City",
                style: styles.gLinkBankInput,
              }}
              uniqueName="addresses0city"
              myTitle="City*"
              rules={{ required: { message: "Required" } }}
            />
            <Spacer />
            <ValidatedTextFormInput<tAddressSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_ADDRESS",
                address: { state_or_province: "" },
              }}
              dispatchPath={"address.state_or_province"}
              selectorFunction={stateOrProvinceSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "State or Province",
                style: styles.gLinkBankInput,
              }}
              uniqueName="addresses0state_or_province"
              myTitle="State*"
              rules={{ required: { message: "Required" } }}
            />
            <Spacer />
            <ValidatedTextFormInput<tAddressSettingsPath>
              wrapperStyle={styles.gFormRowNoMarginValidatedSingle}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              dispatchAction={{
                type: "ADMIN_UPDATE_ADDRESS",
                address: { postal_code: "" },
              }}
              dispatchPath={"address.postal_code"}
              selectorFunction={postalCodeSelector}
              controller={sIsValid}
              setController={setsIsValid}
              textInputProps={{
                placeholder: "Zipcode",
                style: styles.gLinkBankInput,
              }}
              uniqueName="addresses0postal_code"
              myTitle="Zip*"
              rules={{ required: { message: "Required" } }}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <NumberFormEntry
              myTitle="Opening Order Minimum"
              value={brandSettings.openingMin?.toString()}
              onChangeNumber={onChangeOpeningMin}
            />
            <Spacer />
            <NumberFormEntry
              myTitle="Reorder Minimum"
              value={brandSettings.reorderMin?.toString()}
              onChangeNumber={onChangeReorderMin}
            />
          </View>

          <PdStack
            layout={[1, 1]}
            gap={4}
            direction={rIsMobile ? "vertical" : "horizontal"}
            style={[styles.zIndexTen, styles.my3]}
          >
            <View>
              <ValidatedComboBoxInput<tBrandSettingsPath>
                wrapperStyle={[styles.wFull, styles.flex1]}
                titleViewStyle={[styles.rowNoMargin, styles.formTitleHeight, styles.alignCenter]}
                comboBoxStyle={[styles.formFieldHeight, styles.mt1]}
                dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { shippingLeadTime: "" } }}
                dispatchPath="settingsPart.shippingLeadTime"
                selectorFunction={shippingLeadTimeSelector}
                controller={sIsValid}
                placeholder="1-3 Days"
                setController={setsIsValid}
                uniqueName="shippingLeadTime"
                myTitle="Shipping Lead Time*"
                ComboOptions={aShippingLeadTime}
                rules={{ required: { message: "Required" } }}
              />
            </View>
            <View>
              <View style={[styles.flexRow, styles.alignCenter, styles.rowNoMargin]}>
                <Text style={styles.P09_FormTitle}>Default Shipping Percent</Text>
                {rEstimatingShippingUrl && (
                  <PdButton
                    onPress={() => openUrlInNewTab(rEstimatingShippingUrl)}
                    leftIcon={<PdTruck style={styles.sizeSm} />}
                    variant="ghost"
                    style={styles.mlAuto}
                    contentContainerStyle={styles.px0}
                    size="sm"
                  >
                    <PdButtonLabel style={styles.linkLabel}>Estimate Your shipping</PdButtonLabel>
                  </PdButton>
                )}
              </View>
              <ShimmerView loading={false} style={styles.inputShimmer}>
                <NumberInput
                  unit={{ label: "%", location: "end" }}
                  style={styles.gFormInput}
                  value={formatPercentNumber(brandSettings.defaultShipPercent ?? 0.05).toString()}
                  onChangeNumber={onChangeDefaultShipPercent}
                />
              </ShimmerView>
              <PdText style={[styles.textXxs, styles.colorGray, styles.mt1]}>
                Default shipping will apply to new retailers you connect with.
              </PdText>
              <PdText style={[styles.textXxs, styles.colorGray]}>
                You can change the shipping on a per retailer basis once you and the retailer connect.
              </PdText>
            </View>
          </PdStack>
          <PdStack
            layout={[1, 1]}
            gap={4}
            direction={rIsMobile ? "vertical" : "horizontal"}
            style={rIsMobile ? {} : styles.alignEnd}
          >
            <View>
              <View style={[styles.row, styles.alignCenter, styles.justifyBetween]}>
                <View style={[styles.row, styles.alignCenter]}>
                  <Text style={styles.P09_FormTitle}>Terms and Conditions*</Text>
                  {sIsValid?.termsAndConditions && <Text style={styles.inputErrorLabel}>Please Fix</Text>}
                  <TouchableOpacity onPress={onPressUploadTOS}>
                    <Import_Sm style={styles.P09_AbsoluteImport} />
                  </TouchableOpacity>
                </View>
                {rTermsAndConditionsSampleUrl && (
                  <TouchableOpacity onPress={() => openUrlInNewTab(rTermsAndConditionsSampleUrl)}>
                    <PdText style={styles.termsSampleLink}>
                      {rIsMobile ? "View Sample" : "View Sample Terms & Conditions"}
                    </PdText>
                  </TouchableOpacity>
                )}
              </View>
              <ShimmerView style={styles.inputShimmer}>
                <TouchableOpacity onPress={onPressTermsAndConditions}>
                  <Text
                    ellipsizeMode="tail"
                    numberOfLines={1}
                    style={[
                      styles.gFormInput,
                      !brandSettings.termsAndConditions?.url ? styles.gFormInputPlaceholder : {},
                    ]}
                  >
                    {brandSettings.termsAndConditions.name !== undefined && brandSettings.termsAndConditions.name !== ""
                      ? brandSettings.termsAndConditions.name
                      : "Upload PDF of your Terms and Conditions Policy"}
                  </Text>
                </TouchableOpacity>
              </ShimmerView>
            </View>
            <View>
              <ValidatedTextFormInput<tBrandSettingsPath>
                titleViewStyle={styles.rowNoMargin}
                dispatchAction={{ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { taxId: "" } }}
                dispatchPath="settingsPart.taxId"
                selectorFunction={taxIdSelector}
                controller={sIsValid}
                setController={setsIsValid}
                titleProps={{ style: styles.P08_01_InputHeader }}
                myTitle="Tax ID #"
                uniqueName="taxId"
                textInputProps={{
                  style: styles.gLinkBankInput,
                  placeholder: "Company Tax Id",
                }}
              />
            </View>
          </PdStack>
          <View style={styles.gHeaderSpacing}>
            <Text style={styles.gFormHeader}>Payment Terms</Text>
            <Text style={styles.underPaymentTermsSpacing}>
              Upon Order Shipment, we submit payment to you right away, directly to your bank account. Please click
              below to connect your account to receive payments
            </Text>
            <PdButton
              variant={isSetupPayments ? "outline" : "solid"}
              onPress={handleOnPressSetupAccount}
              style={styles.mrAuto}
            >
              <PdButtonLabel>{isSetupPayments ? "Edit Terms" : "Setup"}</PdButtonLabel>
            </PdButton>
          </View>
        </View>
      </View>
      {rIsAdmin ? (
        <>
          <AdminUserPanel />
          <View style={styles.P09_PageContainer}>
            <View style={[styles.gSpaceBetweenRow, styles.gHeaderSpacing]}>
              <View style={styles.rowColumn}>
                <Text style={styles.gFormHeader}>Admin Abilities</Text>
              </View>
            </View>
            <View style={styles.gLinkBankFormItemYesNo}>
              <Text>Is this company EXCLUSIVE?</Text>
              <YesNo
                myState={brandSettings.isExclusive}
                myText={["No", "Yes"]}
                onPressTrue={() => {
                  dispatch({ type: "ADMIN_UPDATE_BRAND_ISEXCLUSIVE", isExclusive: true });
                }}
                onPressFalse={() => {
                  dispatch({ type: "ADMIN_UPDATE_BRAND_ISEXCLUSIVE", isExclusive: false });
                }}
              />
            </View>
            <View style={styles.gLinkBankFormItemYesNo}>
              <Text>Publish brand to retailers?</Text>
              <YesNo
                myState={brandSettings.pauseMode !== "pause"}
                myText={["No", "Yes"]}
                onPressTrue={handleBrandPublish}
                onPressFalse={handleBrandHide}
              />
            </View>
            <View style={styles.gFormRowNoMargin}>
              <View style={styles.gLinkBankFormItem}>
                <Text>Commission Fee</Text>
                <NumberInput
                  value={formatInputPercent((brandSettings.commissionFee ?? 0) * 100)}
                  unit={{ label: "%", location: "end" }}
                  maxLength={4}
                  onChangeNumber={(myFee) => {
                    dispatch({ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { commissionFee: myFee / 100 } });
                  }}
                  style={styles.gLinkBankInput}
                />
              </View>
              <View style={styles.gLinkBankFormItemBottomMargin}>
                <Text>Service Fee</Text>
                <NumberInput
                  value={formatInputPercent((brandSettings.serviceFee ?? 0) * 100)}
                  maxLength={4}
                  onChangeNumber={(myFee) =>
                    dispatch({ type: "ADMIN_UPDATE_BRAND_SETTINGS", settingsPart: { serviceFee: myFee / 100 } })
                  }
                  unit={{ label: "%", location: "end" }}
                  style={styles.gLinkBankInput}
                />
              </View>
            </View>
            <View style={styles.P03_01ButtonContainerAdmin}>
              <StandardButton
                type="primary"
                text="SAVE"
                onPress={() => {
                  dispatch(sendInfo(brandSettings, rUser));
                }}
                isLoading={rIsProfileUpdating}
              />
            </View>
          </View>
        </>
      ) : (
        <UserSettings isValid={sIsValid} />
      )}
    </ScrollView>
  );
};

const BrandSettings: FC = () => {
  const route = useRoute<tAdminBrandSettingsParams>();
  useFetchCompanySettings(route?.params?.brandId, "brands");
  const dispatch = useAppDispatch();
  const isLoading = useSelector((state) => state.LoadingReducer.companyFetching);
  const adminSettings = useSelector((state) => state.AdminSettingReducer);

  useEffect(() => {
    dispatch(setBackButtonRoute(kRoute.P06));
  }, [dispatch]);

  if (
    isLoading ||
    !adminSettings ||
    adminSettings.type === "none" ||
    (adminSettings.type === "brand" && !adminSettings.id)
  ) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  if (adminSettings.type !== "brand") {
    return null;
  }

  return <Content brandSettings={adminSettings} />;
};

export default BrandSettings;

const styles = StyleSheet.create({
  P03_01ButtonContainerAdmin: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginVertical: 8 * kStyleConst.kSpacing,
  },
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  P08_PageHeader: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  P09_AbsoluteImport: {
    ...kStyleConst.kImportSmall,
    alignSelf: "center",
    marginLeft: 3 * kStyleConst.kSpacing,
  },
  P09_Banner: {
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    flexDirection: "column",
    height: kStyleConst.kBannerHeight,
    justifyContent: "center",
    marginBottom: 141 / 2,
  },
  P09_BannerImage: {
    height: 197,
    width: "100%",
  },
  P09_Column: {
    flexDirection: "column",
    flex: 1,
    flexWrap: "wrap",
    minWidth: kStyleConst.kMinWidth,
  },
  P09_DoubleShimmer: {
    borderColor: kColors.brandPink,
    borderWidth: 1,
    height: 165,
    width: "100%",
  },
  P09_FormTitle: {
    ...kFonts.formLegend,
  },
  P09_HeroImages: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    padding: 6 * kStyleConst.kSpacing,
  },
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
  P09_ImageSelectRound: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 360,
    flexDirection: "column",
    justifyContent: "center",
    marginTop: kStyleConst.kSpacing * 4,
  },
  P09_ImageSelectSquare: {
    ...kStyleConst.kImageSelectPrimary,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    justifyContent: "center",
  },
  P09_PageContainer: {
    backgroundColor: kColors.white,
    flexGrow: 1,
    marginRight: 6 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
  },
  P09_ProfileImageContainerValid: {
    bottom: 10,
    left: 10,
    marginTop: kStyleConst.kSpacing * 2,
    position: "absolute",
    width: kStyleConst.kImageSelect.width,
  },
  P09_Scroll: {
    flex: 1,
    width: "100%",
  },
  P09_ScrollContentContainer: {
    alignItems: "center",
  },
  P09_StackScreenDefault: {
    backgroundColor: kColors.brandLight,
    flex: 1,
  },
  P09_ZTop: {
    zIndex: 10,
  },
  P09_ZeroTop: {
    marginTop: 0,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  colorGray: {
    color: kColors.gray500,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  formControl: {
    marginBottom: 16,
  },
  formFieldHeight: {
    height: 36,
  },
  formTitleHeight: {
    height: 32,
  },
  gFormHeader: {
    ...kFonts.profileHeader,
    marginBottom: kStyleConst.kSpacing * 2,
  },
  gFormInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginTop: 5,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingRight: 40,
    paddingVertical: 10,
    width: "100%",
  },
  gFormInputPlaceholder: {
    color: kColors.mediumGrey,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginValidatedSingle: {
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gHeaderSpacing: {
    marginVertical: 15,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemBottomMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMarginZIndexSingle: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
    minWidth: kStyleConst.kApplyMinWidth,
    zIndex: 10,
  },
  gLinkBankFormItemYesNo: {
    flexDirection: "row",
    flexGrow: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputBox: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: kStyleConst.kApplicationNotesBoxHeight,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gSpaceBetweenRow: {
    alignItems: "baseline",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  imageBlank: {
    ...kStyleConst.kImageSelectPrimary,
    alignItems: "center",
    backgroundColor: kColors.VeryLightGrey,
    justifyContent: "center",
    margin: kStyleConst.kSpacing * 2,
  },
  inputErrorLabel: {
    color: kColors.red,
    paddingLeft: kStyleConst.kSpacing,
    zIndex: 12,
  },
  inputHeaderCarouselImage: {
    ...kFonts.formLegend,
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  inputShimmer: {
    height: 43,
    width: "100%",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  linkLabel: {
    fontSize: 10,
    textDecorationLine: "underline",
  },
  margin: {
    margin: kStyleConst.kSpacing * 2,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt1: {
    marginTop: kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  my3: {
    marginVertical: 3 * kStyleConst.kSpacing,
  },
  px0: {
    paddingHorizontal: 0,
  },
  row: {
    flexDirection: "row",
    position: "relative",
    zIndex: 1000,
  },
  rowColumn: {
    flex: 1,
    marginBottom: 16,
    paddingEnd: 16,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
  sizeSm: {
    width: 20,
  },
  termsSampleLink: {
    fontSize: 10,
    textDecorationLine: "underline",
  },
  textXxs: {
    fontSize: 10,
  },
  underPaymentTermsSpacing: { marginBottom: 7 * kStyleConst.kSpacing },
  wFull: {
    width: "100%",
  },
  zIndexTen: {
    zIndex: 10,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  P09_ImageSelectSquare: {
    ...kStyleConst.kImageSelect,
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    justifyContent: "center",
  },
  P09_ImageSelectSquarePrimary: {
    ...kStyleConst.kImageSelectPrimary,
    backgroundColor: kColors.backgroundGrey,
    margin: kStyleConst.kSpacing * 2,
  },
  P08_01_VariantImage: {
    height: 57,
    width: 57,
  },
  P09_BannerImage: {
    height: 197,
    width: "100%",
  },
  P09_ImageRound: {
    ...kStyleConst.kImageSelect,
    borderRadius: 360,
  },
  P09_ImageSquare: {
    ...kStyleConst.kImageSelect,
  },
} as const;
